import { useState, useRef, useCallback, useEffect } from 'react';

interface GalleryItem {
  type: 'image' | 'video';
  src: string;
  thumb?: string;
  videoId?: string; // YouTube video ID
}

interface Props {
  images: string[];
  badge?: string;
  badgeColor?: string;
  discount?: number;
  color: string;
  productName: string;
}

/* ── Lightbox ── */
const Lightbox = ({
  items, activeIdx, onClose, onPrev, onNext, color,
}: {
  items: GalleryItem[];
  activeIdx: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  color: string;
}) => {
  const item = items[activeIdx];

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{ background: 'rgba(5,5,8,0.96)', backdropFilter: 'blur(12px)' }}
      onClick={onClose}
    >
      {/* Scanlines */}
      <div className="absolute inset-0 scanline opacity-10 pointer-events-none" />

      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-5 right-5 z-10 w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200"
        style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,45,120,0.2)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)'; }}
      >
        <i className="ri-close-line text-white text-xl" />
      </button>

      {/* Counter */}
      <div
        className="absolute top-5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full font-mono text-xs"
        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
      >
        {activeIdx + 1} / {items.length}
      </div>

      {/* Prev */}
      {activeIdx > 0 && (
        <button
          onClick={e => { e.stopPropagation(); onPrev(); }}
          className="absolute left-4 md:left-8 z-10 w-12 h-12 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}60`; (e.currentTarget as HTMLElement).style.background = `${color}15`; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.12)'; (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
        >
          <i className="ri-arrow-left-s-line text-white text-2xl" />
        </button>
      )}

      {/* Next */}
      {activeIdx < items.length - 1 && (
        <button
          onClick={e => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 md:right-8 z-10 w-12 h-12 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}60`; (e.currentTarget as HTMLElement).style.background = `${color}15`; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.12)'; (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
        >
          <i className="ri-arrow-right-s-line text-white text-2xl" />
        </button>
      )}

      {/* Content */}
      <div
        className="relative max-w-4xl w-full mx-16 flex items-center justify-center"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: '80vh' }}
      >
        {item.type === 'video' ? (
          <div className="w-full rounded-2xl overflow-hidden" style={{ aspectRatio: '16/9', border: `1px solid ${color}30` }}>
            <iframe
              src={`https://www.youtube.com/embed/${item.videoId}?autoplay=1&rel=0&modestbranding=1`}
              title="Видеообзор товара"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        ) : (
          <img
            src={item.src}
            alt=""
            className="max-w-full max-h-[75vh] object-contain rounded-2xl"
            style={{ filter: `drop-shadow(0 0 40px ${color}30)` }}
          />
        )}
      </div>

      {/* Thumbnail strip */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
        {items.map((it, i) => (
          <button
            key={i}
            onClick={e => { e.stopPropagation(); }}
            onMouseDown={e => { e.stopPropagation(); }}
            className="w-12 h-12 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 flex items-center justify-center shrink-0"
            style={{
              border: i === activeIdx ? `2px solid ${color}` : '2px solid rgba(255,255,255,0.1)',
              background: '#0d0d1a',
              boxShadow: i === activeIdx ? `0 0 10px ${color}50` : 'none',
            }}
          >
            {it.type === 'video' ? (
              <div className="w-full h-full flex items-center justify-center" style={{ background: 'rgba(255,45,120,0.15)' }}>
                <i className="ri-play-fill text-white/70 text-lg" />
              </div>
            ) : (
              <img src={it.thumb ?? it.src} alt="" className="w-full h-full object-contain" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

/* ── Zoom lens ── */
const ZoomImage = ({
  src, alt, color, onClick,
}: {
  src: string;
  alt: string;
  color: string;
  onClick: () => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(false);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });
  const [resultPos, setResultPos] = useState({ x: 0, y: 0 });
  const ZOOM_FACTOR = 2.5;
  const LENS_SIZE = 100;

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    let x = e.clientX - rect.left - LENS_SIZE / 2;
    let y = e.clientY - rect.top - LENS_SIZE / 2;
    x = Math.max(0, Math.min(x, rect.width - LENS_SIZE));
    y = Math.max(0, Math.min(y, rect.height - LENS_SIZE));
    setLensPos({ x, y });
    const rx = (x / (rect.width - LENS_SIZE)) * 100;
    const ry = (y / (rect.height - LENS_SIZE)) * 100;
    setResultPos({ x: rx, y: ry });
  }, []);

  return (
    <div className="relative w-full h-full">
      {/* Main image container */}
      <div
        ref={containerRef}
        className="relative w-full h-full flex items-center justify-center cursor-crosshair select-none"
        onMouseEnter={() => setZoom(true)}
        onMouseLeave={() => setZoom(false)}
        onMouseMove={handleMouseMove}
        onClick={onClick}
      >
        <img
          src={src}
          alt={alt}
          className="w-64 h-64 object-contain relative z-10 transition-transform duration-300"
          style={{ filter: `drop-shadow(0 16px 32px ${color}55)`, transform: zoom ? 'scale(1.03)' : 'scale(1)' }}
          draggable={false}
        />

        {/* Zoom lens */}
        {zoom && (
          <div
            className="absolute pointer-events-none z-20 rounded-full overflow-hidden"
            style={{
              width: LENS_SIZE,
              height: LENS_SIZE,
              left: lensPos.x,
              top: lensPos.y,
              border: `2px solid ${color}`,
              boxShadow: `0 0 0 1px rgba(0,0,0,0.3), 0 0 15px ${color}40`,
            }}
          />
        )}

        {/* Zoom hint */}
        {!zoom && (
          <div
            className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full font-mono text-[10px] pointer-events-none z-10 transition-opacity"
            style={{ background: 'rgba(5,5,8,0.7)', border: `1px solid ${color}30`, color: `${color}cc`, backdropFilter: 'blur(4px)' }}
          >
            <i className="ri-zoom-in-line text-xs" />
            Наведите для увеличения
          </div>
        )}

        {/* Click to fullscreen hint */}
        {zoom && (
          <div
            className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full font-mono text-[10px] pointer-events-none z-20"
            style={{ background: 'rgba(5,5,8,0.8)', border: `1px solid ${color}40`, color: `${color}cc`, backdropFilter: 'blur(4px)' }}
          >
            <i className="ri-fullscreen-line text-xs" />
            Клик — полный экран
          </div>
        )}
      </div>

      {/* Zoom result panel — appears to the right */}
      {zoom && (
        <div
          className="absolute top-0 left-[calc(100%+12px)] w-64 h-64 rounded-2xl overflow-hidden pointer-events-none z-30 hidden xl:block"
          style={{
            border: `1px solid ${color}40`,
            boxShadow: `0 0 30px ${color}20`,
            backgroundImage: `url(${src})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: `${ZOOM_FACTOR * 100}%`,
            backgroundPosition: `${resultPos.x}% ${resultPos.y}%`,
          }}
        >
          <div className="absolute inset-0 scanline opacity-10" />
          <div
            className="absolute top-2 left-2 px-2 py-1 rounded-full font-mono text-[9px]"
            style={{ background: 'rgba(5,5,8,0.8)', color: `${color}cc`, border: `1px solid ${color}30` }}
          >
            ×{ZOOM_FACTOR}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Main Gallery Component ── */
const ProductGallery = ({ images, badge, badgeColor, discount, color, productName }: Props) => {
  // YouTube video ID for the review
  const VIDEO_ID = 'dQw4w9WgXcQ';

  const items: GalleryItem[] = [
    ...images.map(src => ({ type: 'image' as const, src, thumb: src })),
    {
      type: 'video',
      src: `https://img.youtube.com/vi/${VIDEO_ID}/hqdefault.jpg`,
      thumb: `https://img.youtube.com/vi/${VIDEO_ID}/default.jpg`,
      videoId: VIDEO_ID,
    },
  ];

  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const activeItem = items[activeIdx];

  const openLightbox = () => setLightboxOpen(true);
  const closeLightbox = () => setLightboxOpen(false);
  const goPrev = () => setActiveIdx(i => Math.max(0, i - 1));
  const goNext = () => setActiveIdx(i => Math.min(items.length - 1, i + 1));

  return (
    <>
      {lightboxOpen && (
        <Lightbox
          items={items}
          activeIdx={activeIdx}
          onClose={closeLightbox}
          onPrev={goPrev}
          onNext={goNext}
          color={color}
        />
      )}

      <div className="flex flex-col gap-3">
        {/* Main viewer */}
        <div
          className="relative rounded-2xl overflow-hidden"
          style={{
            height: '420px',
            background: `linear-gradient(135deg, #0d0d1a 0%, ${color}07 100%)`,
            border: `1px solid ${color}18`,
          }}
        >
          {/* Badges */}
          <div className="absolute top-4 left-4 flex flex-col gap-2 z-10">
            {badge && (
              <div
                className="px-3 py-1.5 rounded-full font-orbitron font-bold text-xs"
                style={{ background: badgeColor, color: '#050508', boxShadow: `0 0 10px ${badgeColor}80` }}
              >
                {badge}
              </div>
            )}
          </div>

          {discount && (
            <div
              className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center rounded-full font-orbitron font-black text-sm z-10"
              style={{ background: `${color}18`, border: `2px solid ${color}`, color }}
            >
              -{discount}%
            </div>
          )}

          {/* Fullscreen button */}
          <button
            onClick={openLightbox}
            className="absolute top-4 right-16 z-10 w-9 h-9 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200"
            style={{ background: 'rgba(5,5,8,0.6)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(4px)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}60`; (e.currentTarget as HTMLElement).style.background = `${color}20`; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.12)'; (e.currentTarget as HTMLElement).style.background = 'rgba(5,5,8,0.6)'; }}
            title="Открыть на весь экран"
          >
            <i className="ri-fullscreen-line text-white/60 text-sm" />
          </button>

          {/* Ambient glow */}
          <div
            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-56 h-28 rounded-full opacity-15 pointer-events-none"
            style={{ background: color, filter: 'blur(40px)' }}
          />

          {/* Corner accents */}
          <div className="absolute top-3 left-3 w-5 h-5 pointer-events-none" style={{ borderTop: `1px solid ${color}50`, borderLeft: `1px solid ${color}50` }} />
          <div className="absolute bottom-3 right-3 w-5 h-5 pointer-events-none" style={{ borderBottom: `1px solid ${color}50`, borderRight: `1px solid ${color}50` }} />

          {/* Content */}
          {activeItem.type === 'video' ? (
            /* Video preview with play button */
            <div className="w-full h-full flex items-center justify-center relative cursor-pointer group" onClick={openLightbox}>
              <img
                src={activeItem.src}
                alt="Видеообзор"
                className="w-full h-full object-cover opacity-40"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                <div
                  className="w-20 h-20 flex items-center justify-center rounded-full transition-all duration-300 group-hover:scale-110"
                  style={{
                    background: 'rgba(255,45,120,0.2)',
                    border: '2px solid #ff2d78',
                    boxShadow: '0 0 30px rgba(255,45,120,0.4)',
                  }}
                >
                  <i className="ri-play-fill text-white text-4xl ml-1" />
                </div>
                <div className="text-center">
                  <p className="font-orbitron font-bold text-white text-sm">Видеообзор товара</p>
                  <p className="font-mono text-xs text-white/40 mt-1">{productName}</p>
                </div>
                <div
                  className="flex items-center gap-2 px-4 py-2 rounded-full font-mono text-xs"
                  style={{ background: 'rgba(5,5,8,0.7)', border: '1px solid rgba(255,45,120,0.3)', color: '#ff2d78', backdropFilter: 'blur(8px)' }}
                >
                  <i className="ri-youtube-line" />
                  Смотреть на YouTube
                </div>
              </div>
            </div>
          ) : (
            /* Zoomable image */
            <ZoomImage
              src={activeItem.src}
              alt={productName}
              color={color}
              onClick={openLightbox}
            />
          )}

          {/* Nav arrows inside main viewer */}
          {activeIdx > 0 && (
            <button
              onClick={goPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200"
              style={{ background: 'rgba(5,5,8,0.7)', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(4px)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}60`; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.1)'; }}
            >
              <i className="ri-arrow-left-s-line text-white/70 text-lg" />
            </button>
          )}
          {activeIdx < items.length - 1 && (
            <button
              onClick={goNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200"
              style={{ background: 'rgba(5,5,8,0.7)', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(4px)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}60`; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.1)'; }}
            >
              <i className="ri-arrow-right-s-line text-white/70 text-lg" />
            </button>
          )}

          {/* Dot indicators */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                className="cursor-pointer transition-all duration-300 rounded-full"
                style={{
                  width: i === activeIdx ? '20px' : '6px',
                  height: '6px',
                  background: i === activeIdx ? color : 'rgba(255,255,255,0.25)',
                  boxShadow: i === activeIdx ? `0 0 6px ${color}` : 'none',
                }}
              />
            ))}
          </div>
        </div>

        {/* Image disclaimer */}
        <p className="font-mono text-[11px] text-white/28 text-center flex items-center justify-center gap-1.5">
          <i className="ri-image-line text-white/20" />
          Внешний вид товара может отличаться от изображения
        </p>

        {/* Thumbnails */}
        <div className="flex gap-2.5 flex-wrap">
          {items.map((item, i) => (
            <button
              key={i}
              onClick={() => setActiveIdx(i)}
              className="w-[72px] h-[72px] rounded-xl overflow-hidden cursor-pointer transition-all duration-300 flex items-center justify-center shrink-0 relative group"
              style={{
                background: '#0d0d1a',
                border: activeIdx === i ? `2px solid ${color}` : '2px solid rgba(255,255,255,0.07)',
                boxShadow: activeIdx === i ? `0 0 10px ${color}45` : 'none',
              }}
            >
              {item.type === 'video' ? (
                <>
                  <img src={item.thumb ?? item.src} alt="Видео" className="w-full h-full object-cover opacity-60" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className="w-7 h-7 flex items-center justify-center rounded-full transition-all duration-200 group-hover:scale-110"
                      style={{ background: 'rgba(255,45,120,0.8)', boxShadow: '0 0 8px rgba(255,45,120,0.5)' }}
                    >
                      <i className="ri-play-fill text-white text-sm ml-0.5" />
                    </div>
                  </div>
                  {/* Video label */}
                  <div
                    className="absolute bottom-0 left-0 right-0 py-0.5 text-center font-mono text-[8px]"
                    style={{ background: 'rgba(5,5,8,0.8)', color: '#ff2d78' }}
                  >
                    ВИДЕО
                  </div>
                </>
              ) : (
                <img src={item.src} alt="" className="w-12 h-12 object-contain transition-transform duration-200 group-hover:scale-105" />
              )}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default ProductGallery;
